import { useRef, useEffect, useState } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import { app } from '@configuration/firebaseConfig'
import { mapUid } from '@helpers/authenticateMapUid'
import { logger } from '@helpers/logger'
import { postUser, updateUserLogin, patchUser } from '@services/userService'
import { getFeaturedTournamentPoolsSummary } from '@providers/services/featuredPoolsService'
import { getActiveTournamentPools } from '@providers/services/getActiveTournamentPools'
import { getCompletedTournamentPools } from '@providers/services/getCompletedTournamentPools'

performance.mark('auth-init')

// Keep track of chat loading state
let isChatLoaded = false // eslint-disable-line
let chatLoadingPromise = null

const featuredTournamentPoolsSummaryPromise = getFeaturedTournamentPoolsSummary()

// Function to load the Chat
const loadChat = () => {
  if (!chatLoadingPromise) {
    chatLoadingPromise = import('@chat/ChatApp').then((module) => {
      isChatLoaded = true
      return module.default
    })
  }
  return chatLoadingPromise
}

export const useAuth = ({
  locale,
  changeLocale,
  setActivePage,
  setUserDoc,
  setActiveTournamentPoolData,
  setCompletedTournamentPoolData,
  setLeaguesData,
  setFeaturedTournamentPoolsSummaryData,
  setHasActiveTournaments,
  setHasCompletedTournaments,
  setError,
  setCheckedAuthenticated,
  getSportsLeaguesDataRef,
  dataFetchedRef,
  isChatLoaded,
  setIsChatLoaded,
}) => {
  const userRef = useRef(null)
  const authInitializedRef = useRef(false)
  const disableStrictDoubleInvoke = false
  const db = getFirestore(app)
  const [showLoginScreen, setShowLoginScreen] = useState(true)

  useEffect(() => {
    const auth = getAuth(app)
    const unsubscribe = onAuthStateChanged(auth, async (newUser) => {
      performance.mark('auth-state-change-start')

      try {
        if (newUser) {
          // Map the user's UID
          newUser.uid = mapUid(newUser.uid)

          // When the flag is disabled (false), we only run the sign-in logic once.
          if (disableStrictDoubleInvoke) {
            if (authInitializedRef.current) return
            authInitializedRef.current = true
          }

          logger.checkpoint('---  Authenticated ---')
          userRef.current = newUser

          // Set showLoginScreen to false after user is set
          setShowLoginScreen(false)

          const userDocRef = doc(db, 'users', newUser.uid)
          const userDocSnap = await getDoc(userDocRef)

          if (userDocSnap.exists()) {
            let userData = userDocSnap.data()

            // Update login information
            updateUserLogin()
            if (userData.locale !== locale) {
              userData.locale = locale
              setUserDoc(userData)
              patchUser({ displayName: userData.displayName, locale, avatar: userData.avatar }, setUserDoc)
            } else {
              await changeLocale(userData.locale || 'en-za')
              // Only navigate to page 3 during initial authentication, not on every locale change
              setActivePage(3)
            }

            setUserDoc(userData)
          } else {
            try {
              await postUser()
              updateUserLogin()
              setUserDoc({})
            } catch (error) {
              throw new Error(error)
            }
          }

          performance.mark('user-doc-fetched-or-created')
          performance.measure('Auth: User doc fetch/create', 'auth-state-change-start', 'user-doc-fetched-or-created')

          // Fetch data only if it hasn't been fetched yet
          if (!dataFetchedRef.current) {
            if (document.getElementById('firebaseui-auth-container')) {
              document.getElementById('firebaseui-auth-container').style.opacity = 0
            }

            try {
              logger.logWithTime('activeTournamentPoolsLoading', 'DataProvider.jsx', 'Starting to fetch featured tournament pools')
              const featuredTournamentPoolsSummary = await featuredTournamentPoolsSummaryPromise
              logger.logWithTime('activeTournamentPoolsLoading', 'DataProvider.jsx', 'Featured tournament pools fetched', {
                count: featuredTournamentPoolsSummary?.length || 0,
                tournamentKeys: featuredTournamentPoolsSummary?.map((pool) => pool.tournamentKey),
              })

              setFeaturedTournamentPoolsSummaryData(featuredTournamentPoolsSummary)

              logger.logWithTime('activeTournamentPoolsLoading', 'DataProvider.jsx', 'Starting to fetch active tournament pools')
              // Fetch joined and completed tournament pools
              getActiveTournamentPools(
                featuredTournamentPoolsSummary,
                (userTournamentEntry) => {
                  logger.logWithTime('activeTournamentPoolsLoading', 'DataProvider.jsx', 'Active tournament pool callback received', {
                    tournamentKey: userTournamentEntry?.tournamentKey,
                    eventsCount: userTournamentEntry?.userEntry?.userTournamentPoolEntryEvents?.length || 0,
                  })
                  setActiveTournamentPoolData((prevData) => {
                    if (prevData) {
                      if (!userTournamentEntry) return prevData
                      const tournamentKeyExists = prevData.some((entry) => entry.tournamentKey === userTournamentEntry.tournamentKey)
                      if (!tournamentKeyExists) {
                        return [...prevData]
                        // return [...prevData, userTournamentEntry] // this was the original line
                      }
                      return prevData
                    } else {
                      if (!userTournamentEntry) return ''
                      return [userTournamentEntry]
                    }
                  })
                },
                setHasActiveTournaments,
              ).then(() => {
                getCompletedTournamentPools((userTournamentEntry) => {
                  setCompletedTournamentPoolData((prevData) => {
                    if (prevData) {
                      if (!userTournamentEntry) return prevData
                      const newEntries = Array.isArray(userTournamentEntry) ? userTournamentEntry : [userTournamentEntry]
                      const validEntries = newEntries.filter((entry) => entry && entry.tournamentData)
                      const tournamentKeyExists = prevData.some((entry) => entry.tournamentData?.tournamentKey === validEntries[0]?.tournamentData?.tournamentKey)
                      if (!tournamentKeyExists) {
                        return [...prevData, ...validEntries]
                      }
                      return prevData
                    } else {
                      if (!userTournamentEntry) return []
                      const newEntries = Array.isArray(userTournamentEntry) ? userTournamentEntry : [userTournamentEntry]
                      return newEntries.filter((entry) => entry && entry.tournamentData)
                    }
                  })
                }, setHasCompletedTournaments)

                // Start loading chat before getting tournament pools
                if (!isChatLoaded) {
                  loadChat().then(() => {
                    setIsChatLoaded(true)
                  })
                }
              })

              getSportsLeaguesDataRef.current()
              dataFetchedRef.current = true
              window.app.userAuthenticatedTimeStamp = Date.now()
              performance.mark('tournament-league-data-fetched')
            } catch (error) {
              setError(error)
              return
            }
          }
        } else {
          // On sign out, reset everything
          authInitializedRef.current = false
          userRef.current = null
          setUserDoc(null)
          setActiveTournamentPoolData(undefined)
          setCompletedTournamentPoolData(undefined)
          setLeaguesData({ sports: {} })
          dataFetchedRef.current = false
          setCheckedAuthenticated(true)

          // Set showLoginScreen to true when user is null
          setShowLoginScreen(true)
        }
        setCheckedAuthenticated(true)
        performance.mark('auth-state-change-end')
        performance.measure('Auth: Total effect', 'auth-state-change-start', 'auth-state-change-end')
      } catch (error) {
        setError(error)
        setCheckedAuthenticated(true)
        performance.mark('auth-state-change-error')
        performance.measure('Auth: Error', 'auth-state-change-start', 'auth-state-change-error')
        return
      }
    })

    return () => {
      unsubscribe()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return { userRef, showLoginScreen }
}
